import { type ApolloClient, gql, type NormalizedCacheObject } from '@apollo/client/core';
import type {
  ConvertHoldToTicketMutationInput, DeleteTicketBlueprintMutationInput, Mutation, Query, QueryTicketPricingPreviewArgs,
  ReorderTicketBlueprintsMutationInput, SaveTicketBlueprintMutationInput,
} from '../../../generated/api.graphql';
import * as VPErrors from '../../../VPError';
import type { VPResult } from '../../VPResult';
import { VP_ERROR_FIELDS } from '../../VPError/fragments';
import * as TicketBlueprint from './TicketBlueprint';
import * as TicketBlueprintInput from './TicketBlueprintInput';
import * as TicketHold from './TicketHold';
import * as TicketPricingBreakdown from './TicketPricingBreakdown';
import { TICKET_BLUEPRINT_FIELDS } from './fragments';

export const TICKET_BLUEPRINTS_QUERY = gql`
  query TicketBlueprints($eventId: Int!) {
    ticketBlueprints(eventId: $eventId) {
      ...TicketBlueprintFields
    }
  }
  ${TICKET_BLUEPRINT_FIELDS}
`;

export const TICKET_PRICING_PREVIEW_QUERY = gql`
  query TicketPricingPreview($ticketBlueprint: TicketBlueprintAttributes!) {
    ticketPricingPreview(ticketBlueprint: $ticketBlueprint) {
      price
      venueFee
      platformFee
      ccFee
      taxFee
      displayPrice
    }
  }
`;

/** Prices an unsaved ticket with the event's fee and tax settings (ADR-014 decision 2); only eventId, price, fee and sectionId count. */
export const fetchTicketPricingPreview = async (
  client: ApolloClient<NormalizedCacheObject>,
  ticketBlueprint: TicketBlueprintInput.TicketBlueprintInput,
): Promise<VPResult<TicketPricingBreakdown.TicketPricingBreakdown, VPErrors.VPError[]>> => {
  const { data } = await client.query<Query, QueryTicketPricingPreviewArgs>({
    query: TICKET_PRICING_PREVIEW_QUERY,
    fetchPolicy: 'no-cache',
    variables: { ticketBlueprint: TicketBlueprintInput.toGqlInput(ticketBlueprint) },
  });

  if (!data?.ticketPricingPreview) {
    return { success: false, errors: [{ path: '', message: 'Failed to price the ticket.' }] };
  }

  return { success: true, data: TicketPricingBreakdown.fromGql(data.ticketPricingPreview) };
};

export const SAVE_TICKET_BLUEPRINT_MUTATION = gql`
  mutation saveTicketBlueprint($input: SaveTicketBlueprintMutationInput!) {
    saveTicketBlueprint(input: $input) {
      ticketBlueprint {
        ...TicketBlueprintFields
      }
      errors {
        ...VPErrorFields
      }
    }
  }
  ${TICKET_BLUEPRINT_FIELDS}
  ${VP_ERROR_FIELDS}
`;

/** An event's tickets on the tenant (AppSchema) connection; the server refuses the read without `view_tickets?`. */
export const fetchTicketBlueprints = async (
  client: ApolloClient<NormalizedCacheObject>,
  eventId: number,
): Promise<VPResult<TicketBlueprint.TicketBlueprint[], VPErrors.VPError[]>> => {
  const { data } = await client.query<Query>({
    query: TICKET_BLUEPRINTS_QUERY,
    fetchPolicy: 'network-only',
    variables: { eventId },
  });

  if (!data?.ticketBlueprints) {
    return { success: false, errors: [{ path: '', message: 'Failed to fetch the tickets.' }] };
  }

  return { success: true, data: data.ticketBlueprints.map(TicketBlueprint.fromGql) };
};

export const TICKET_HOLDS_QUERY = gql`
  query TicketHolds($eventId: Int!) {
    event(id: $eventId) {
      id
      ticketHolds {
        id
        eventId
        name
        quantity
        orderPriority
        sourceTicketId
        description
      }
    }
  }
`;

/** An event's holds, gated on `view_tickets?` as its tickets are; a missing event fails rather than reading as no holds. */
export const fetchTicketHolds = async (
  client: ApolloClient<NormalizedCacheObject>,
  eventId: number,
): Promise<VPResult<TicketHold.TicketHold[], VPErrors.VPError[]>> => {
  const { data } = await client.query<Query>({
    query: TICKET_HOLDS_QUERY,
    fetchPolicy: 'network-only',
    variables: { eventId },
  });

  if (!data?.event) {
    return { success: false, errors: [{ path: '', message: 'Failed to fetch the ticket holds.' }] };
  }

  return { success: true, data: data.event.ticketHolds.map(TicketHold.fromGql) };
};

/** Creates when the input carries no `id`; the server refuses the write without `edit_tickets?`, and that rejection propagates. */
export const saveTicketBlueprint = async (
  client: ApolloClient<NormalizedCacheObject>,
  ticketBlueprint: TicketBlueprintInput.TicketBlueprintInput,
): Promise<VPResult<TicketBlueprint.TicketBlueprint, VPErrors.VPError[]>> => {
  const input: SaveTicketBlueprintMutationInput = { ticketBlueprint: TicketBlueprintInput.toGqlInput(ticketBlueprint) };

  const { data } = await client.mutate<Mutation>({
    mutation: SAVE_TICKET_BLUEPRINT_MUTATION,
    variables: { input },
  });

  const payload = data?.saveTicketBlueprint;

  const errors = VPErrors.fromGqlCollection(payload?.errors ?? []);

  if (errors.length) {
    return { success: false, errors };
  }

  if (!payload?.ticketBlueprint) {
    return { success: false, errors: [{ path: '', message: 'Failed to save the ticket.' }] };
  }

  return { success: true, data: TicketBlueprint.fromGql(payload.ticketBlueprint) };
};

export const CONVERT_HOLD_TO_TICKET_MUTATION = gql`
  mutation convertHoldToTicket($input: ConvertHoldToTicketMutationInput!) {
    convertHoldToTicket(input: $input) {
      ticketBlueprint {
        ...TicketBlueprintFields
      }
      errors {
        ...VPErrorFields
      }
    }
  }
  ${TICKET_BLUEPRINT_FIELDS}
  ${VP_ERROR_FIELDS}
`;

/** Turns a hold into a sellable ticket that keeps the hold's id, answering with the ticket (ADR-014 decision 5). */
export const convertHoldToTicket = async (
  client: ApolloClient<NormalizedCacheObject>,
  ticketHoldId: number,
  ticketBlueprint: TicketBlueprintInput.TicketBlueprintInput,
): Promise<VPResult<TicketBlueprint.TicketBlueprint, VPErrors.VPError[]>> => {
  const input: ConvertHoldToTicketMutationInput = { ticketHoldId, ticketBlueprint: TicketBlueprintInput.toGqlInput(ticketBlueprint) };

  const { data } = await client.mutate<Mutation>({
    mutation: CONVERT_HOLD_TO_TICKET_MUTATION,
    variables: { input },
  });

  const payload = data?.convertHoldToTicket;

  const errors = VPErrors.fromGqlCollection(payload?.errors ?? []);

  if (errors.length) {
    return { success: false, errors };
  }

  if (!payload?.ticketBlueprint) {
    return { success: false, errors: [{ path: '', message: 'Failed to convert the hold.' }] };
  }

  return { success: true, data: TicketBlueprint.fromGql(payload.ticketBlueprint) };
};

export const DELETE_TICKET_BLUEPRINT_MUTATION = gql`
  mutation deleteTicketBlueprint($input: DeleteTicketBlueprintMutationInput!) {
    deleteTicketBlueprint(input: $input) {
      success
      errors {
        ...VPErrorFields
      }
    }
  }
  ${VP_ERROR_FIELDS}
`;

/** Deletes a ticket; a refusal (e.g. a ticket with sales) resolves with the server's own reason. */
export const deleteTicketBlueprint = async (
  client: ApolloClient<NormalizedCacheObject>,
  ticketBlueprintId: number,
): Promise<VPResult<null, VPErrors.VPError[]>> => {
  const input: DeleteTicketBlueprintMutationInput = { ticketBlueprintId };

  const { data } = await client.mutate<Mutation>({
    mutation: DELETE_TICKET_BLUEPRINT_MUTATION,
    variables: { input },
  });

  const payload = data?.deleteTicketBlueprint;

  if (payload?.success) {
    return { success: true, data: null };
  }

  const errors = VPErrors.fromGqlCollection(payload?.errors ?? []);

  if (errors.length) {
    return { success: false, errors };
  }

  return { success: false, errors: [{ path: '', message: 'Failed to delete the ticket.' }] };
};

export const REORDER_TICKET_BLUEPRINTS_MUTATION = gql`
  mutation reorderTicketBlueprints($input: ReorderTicketBlueprintsMutationInput!) {
    reorderTicketBlueprints(input: $input) {
      ticketBlueprints {
        ...TicketBlueprintFields
      }
      errors {
        ...VPErrorFields
      }
    }
  }
  ${TICKET_BLUEPRINT_FIELDS}
  ${VP_ERROR_FIELDS}
`;

/** Saves an event's ticket priorities in one write; ids left out keep their relative priority after the given ones. */
export const reorderTicketBlueprints = async (
  client: ApolloClient<NormalizedCacheObject>,
  eventId: number,
  ticketBlueprintIds: number[],
): Promise<VPResult<TicketBlueprint.TicketBlueprint[], VPErrors.VPError[]>> => {
  const input: ReorderTicketBlueprintsMutationInput = { eventId, ticketBlueprintIds };

  const { data } = await client.mutate<Mutation>({
    mutation: REORDER_TICKET_BLUEPRINTS_MUTATION,
    variables: { input },
  });

  const payload = data?.reorderTicketBlueprints;

  const errors = VPErrors.fromGqlCollection(payload?.errors ?? []);

  if (errors.length) {
    return { success: false, errors };
  }

  if (!payload?.ticketBlueprints) {
    return { success: false, errors: [{ path: '', message: 'Failed to save the ticket priorities.' }] };
  }

  return { success: true, data: payload.ticketBlueprints.map(TicketBlueprint.fromGql) };
};
